import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import Badge from '@/modules/base/components/badge/badge'
import BackArrowIcon from 'assets/images/icons/arrow_left.svg'
import { useLocation, useNavigate } from 'react-router'

export const VARIANTS = Object.freeze({
  grey: 'grey',
  white: 'white',
})

const useStyles = makeStyles(theme => ({
  container: ({ size, variant }) => ({
    zIndex: 15,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    backgroundColor: variant === VARIANTS.grey ? (theme.superlightGrey || '#f7f7f7') : '#fff',
    width: '100%',
    position: 'sticky',
    top: 0,
    ...sizes[size].container(theme),
  }),
  backButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 20,
    '& a': {
      display: 'flex',
      height: '100%',
      alignItems: 'center',
    },
  },
  backButtonTitle: {
    fontSize: 17,
    paddingLeft: 10,
    textDecoration: 'none',
    color: theme.textLight,
  },
  titleContainer: ({ size, variant, backButtonUrl }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: '0 0 auto',
    margin: 0,
    ...sizes[size].titleContainer(theme, backButtonUrl),
  }),
  subTitle: {
    fontSize: 14,
    marginLeft: 15,
  },
  content: {
    display: 'flex',
    flex: '1 0 auto',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: 30,
  },
  badge: {
    borderRadius: 25,
    color: theme.black,
    border: theme.border,
    padding: '5px 15px',
    display: 'flex',
    width: 'auto',
    justifyContent: 'center',
    fontSize: theme.fontSizes.default,
  },
}))

const Topbar = (props) => {
  const {
    title,
    subTitle,
    badgeText,
    backButtonUrl,
    backButtonTitle,
    children,
    customClass,
    className,
    size = 'default',
    variant = VARIANTS.grey,
    onBackClick,
    classNameChildren,
    dataQsId,
  } = props
  const classes = useStyles({ size, variant, backButtonUrl })
  const navigate = useNavigate()

  const backClick = () => {
    onBackClick?.()
    if (!backButtonUrl && window.history.length > 1) {
      return navigate(-1)
    }

    navigate(backButtonUrl)
  }
  const backButton = backButtonUrl
    ? (
      <div className={classes.backButtonContainer}>
        <a onClick={backClick}>
          <SVGIcon svg={BackArrowIcon} height='15px' width='15px' />
          <span className={classes.backButtonTitle} >{backButtonTitle}</span>
        </a>
      </div>
    )
    : null

  return (
    <div className={clsx(customClass, classes.container, className)} data-qs-id={dataQsId}>
      {backButton}
      <div className={classes.titleContainer}>
        {title}
        <div className={classes.subTitle}>{subTitle}</div>
        {badgeText ? (<Badge label={badgeText} color='white' customClass={classes.badge} />) : null}
      </div>
      <div className={clsx(classes.content, classNameChildren)}>
        {children}
      </div>
    </div>
  )
}

const sizes = {
  default: {
    container: theme => ({
      minHeight: 85,
      maxHeight: 85,
      borderBottom: theme.border,
    }),
    titleContainer: (theme, backButtonUrl) => ({
      fontSize: 24,
      paddingLeft: backButtonUrl ? 10 : 30,
    }),
  },
  small: {
    container: theme => ({
      minHeight: 61,
      maxHeight: 61,
    }),
    titleContainer: theme => ({
      fontSize: 17,
      paddingLeft: 10,
    }),
  },
}

Topbar.propTypes = {
  badgeText: PropTypes.string,
  backButtonTitle: PropTypes.string,
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]),
  subTitle: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]),
  backButtonUrl: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element,
  ]),
  customClass: PropTypes.string,
  className: PropTypes.string,
  classNameChildren: PropTypes.string,
  size: PropTypes.oneOf(['default', 'small']),
  variant: PropTypes.oneOf([VARIANTS.white, VARIANTS.grey]),
  onBackClick: PropTypes.func,
  dataQsId: PropTypes.string.isRequired,
}

export default Topbar
